import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import * as Yup from "yup";
import { request } from "../../../utils/api";
import { toast, ToastContainer } from "react-toastify";
import { FaImage } from "react-icons/fa";
import { makeError, showImage } from "../../../utils";

const AddProduct = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    name: "",
    quantity: "",
    type: "",
    warehouse_id: "",
    images: [], 
  });

  const [imagePreviews, setImagePreviews] = useState([]);
  const [existingImages, setExistingImages] = useState([]);

  const [warehouses, setWarehouses] = useState([]);
  const [errors, setErrors] = useState({});
  const [postLoading, setPostLoading] = useState(false);
  const [loading, setLoading] = useState(false);

  const productSchema = Yup.object().shape({
    name: Yup.string().required("Product name is required"),
    quantity: Yup.number()
      .typeError("Quantity is required")
      .required("Quantity is required")
      .min(1, "Minimum quantity is 1"),
    type: Yup.string().required("Product type is required"),
    warehouse_id: Yup.string().required("Warehouse is required"),
  });

  const inputFields = [
    { label: "Product Name", name: "name", type: "text" },
    { label: "Product Quantity", name: "quantity", type: "number" },
    { label: "Product Type", name: "type", type: "text" },
    { label: "Warehouse", name: "warehouse_id", type: "select" },
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const getProductWarehouse = async () => {
    try {
      const response = await request({ url: "warehouses", method: "GET" });
      setWarehouses(response.data.items);
    } catch {
      toast.error("Error fetching warehouses");
    }
  };

  useEffect(() => {
    getProductWarehouse();
  }, []);

  const validateForm = async () => {
    try {
      await productSchema.validate(formData, { abortEarly: false });
      return true;
    } catch (err) {
      const formattedErrors = {};
      err.inner.forEach((e) => (formattedErrors[e.path] = e.message));
      setErrors(formattedErrors);
      return false;
    }
  };

  const buildFormData = () => {
    const data = new FormData();
    data.append("name", formData.name);
    data.append("quantity", formData.quantity);
    data.append("type", formData.type);
    data.append("warehouse_id", formData.warehouse_id);

    formData.images.forEach((file) => {
      data.append("images[]", file);
    });

    return data;
  };

  const submitProduct = async () => {
    if (!(await validateForm())) return;

    try {
      setPostLoading(true);

      await request({
        url: id ? `products/${id}` : "products",
        method:  "POST",
        data: buildFormData(),
      });

      toast.success(id ? "Product Updated Successfully" : "Product Added Successfully");
      navigate("/product-list");
    } catch (error) {
      makeError(error, toast);
    } finally {
      setPostLoading(false);
    }
  };

  const getProductById = async () => {
    try {
      setLoading(true);

      const response = await request({
        url: `products/${id}`,
        method: "GET",
      });

      const product = response.data;

      setFormData({
        name: product.name,
        quantity: product.quantity,
        type: product.type,
        warehouse_id: product.warehouse.id,
        images: [],
      });

      if (product.images?.length) {
        setExistingImages(product.images.map((img) => showImage(img.url)));
      }
    } catch {
      toast.error("Failed to load product");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) getProductById();
  }, [id]);

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    setFormData((prev) => ({
      ...prev,
      images: [...prev.images, ...files],
    }));

    const previews = files.map((file) => URL.createObjectURL(file));
    setImagePreviews((prev) => [...prev, ...previews]);
  };

  return (
    <>
      <ToastContainer />

      <div className="container-fluid p-0">
        <div className="page-toolbar px-xl-4 px-sm-2 px-0 py-3">
          <ol className="breadcrumb bg-transparent mb-0">
            <li className="breadcrumb-item"><Link to="/">Home</Link></li>
            <li className="breadcrumb-item">Product</li>
            <li className="breadcrumb-item active">{id ? "Update" : "Add"}</li>
          </ol>
        </div>

        <div className="page-body px-xl-4 px-sm-2 px-0 py-lg-2 py-1">
          <div className="card">
            <div className="card-header">
              <h6>{id ? "Update Product" : "Add New Product"}</h6>
            </div>

            <div className="card-body">
              <div className="row g-3">
                {inputFields.map((field) => (
                  <div className="col-md-6" key={field.name}>
                    <label className="form-label">{field.label} *</label>

                    {field.type === "select" ? (
                      <select
                        className="form-select"
                        name="warehouse_id"
                        value={formData.warehouse_id}
                        onChange={handleInputChange}
                      >
                        <option value="">Select Warehouse</option>
                        {warehouses.map((w) => (
                          <option key={w.id} value={w.id}>{w.name}</option>
                        ))}
                      </select>
                    ) : (
                      <input
                        type={field.type}
                        className="form-control"
                        name={field.name}
                        value={formData[field.name]}
                        onChange={handleInputChange}
                      />
                    )}

                    {errors[field.name] && (
                      <small className="text-danger">{errors[field.name]}</small>
                    )}
                  </div>
                ))}
              </div>

              <div className="mt-4">
                <label className="form-label">Product Images</label>

                <div className="input-group">
                  <span className="input-group-text"><FaImage /></span>
                  <input
                    type="file"
                    className="form-control"
                    multiple
                    onChange={handleFileChange}
                  />
                </div>

                {existingImages.length > 0 && (
                  <div className="d-flex gap-2 flex-wrap mt-2">
                    {existingImages.map((img, i) => (
                      <img
                        key={i}
                        src={img}
                        alt=""
                        width={100}
                        height={100}
                        style={{ objectFit: "cover", borderRadius: 8 }}
                      />
                    ))}
                  </div>
                )}

                {imagePreviews.length > 0 && (
                  <div className="d-flex gap-2 flex-wrap mt-2">
                    {imagePreviews.map((img, i) => (
                      <img
                        key={i}
                        src={img}
                        alt=""
                        width={100}
                        height={100}
                        style={{ objectFit: "cover", borderRadius: 8 }}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="card-footer">
              <button className="btn btn-secondary" onClick={() => navigate("/products")}>
                Cancel
              </button>

              <button
                className="btn btn-primary ms-3"
                onClick={submitProduct}
                disabled={postLoading}
              >
                {postLoading ? "Please wait..." : id ? "Update Product" : "Add Product"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AddProduct;
